import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

/**
 * Sorting for every report table: click a heading to sort by it, click again
 * to reverse. Text sorts A to Z, numbers low to high, and blanks always last.
 *
 * The chosen sort is handed back through onChange so a report can save it,
 * which is what keeps it on the pack and in the PDF.
 */

export function compareValues(left, right) {
  const blank = (v) => v === null || v === undefined || v === "";
  if (blank(left) && blank(right)) return 0;
  if (blank(left)) return 1;
  if (blank(right)) return -1;
  const a = typeof left === "number" ? left : Number(left);
  const b = typeof right === "number" ? right : Number(right);
  if (Number.isFinite(a) && Number.isFinite(b) && String(left).trim() !== "" && String(right).trim() !== "") {
    return a - b;
  }
  return String(left).localeCompare(String(right), "en-GB", { numeric: true, sensitivity: "base" });
}

/** A sorted copy. Blanks stay last whichever way the sort runs. */
export function sortItems(items, accessor, direction = "asc") {
  if (!accessor) return items;
  const factor = direction === "desc" ? -1 : 1;
  return [...items].sort((x, y) => {
    const left = accessor(x);
    const right = accessor(y);
    const blank = (v) => v === null || v === undefined || v === "";
    if (blank(left) !== blank(right)) return blank(left) ? 1 : -1;
    return compareValues(left, right) * factor;
  });
}

/**
 * The table's sort, starting from what the report saved.
 *
 * Numbers start high to low on the first click, since "biggest first" is what
 * a board table is usually sorted for; text starts A to Z.
 */
export function useTableSort(saved, onChange) {
  const savedColumn = saved?.column;
  const savedDirection = saved?.direction || "asc";
  const [sort, setSort] = useState(savedColumn ? { column: savedColumn, direction: savedDirection } : null);

  useEffect(() => {
    setSort(savedColumn ? { column: savedColumn, direction: savedDirection } : null);
  }, [savedColumn, savedDirection]);

  const toggle = (column, numeric = false) => {
    setSort((current) => {
      const next = current?.column === column
        ? { column, direction: current.direction === "asc" ? "desc" : "asc" }
        : { column, direction: numeric ? "desc" : "asc" };
      if (onChange) onChange(next);
      return next;
    });
  };
  return [sort, toggle];
}

export function SortHeading({ label, column, sort, onToggle, numeric = false, align = "text-left", className = "" }) {
  const active = sort?.column === column;
  const Icon = !active ? ArrowUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-3 py-2 ${align} ${className}`}>
      <button
        type="button"
        onClick={() => onToggle(column, numeric)}
        className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] hover:text-[#323c44] ${
          active ? "text-[#323c44]" : "text-[#4a5b68]"
        }`}
        title={
          active
            ? `Sorted ${sort.direction === "asc" ? (numeric ? "low to high" : "A to Z") : (numeric ? "high to low" : "Z to A")}. Click to reverse.`
            : `Sort by ${label}`
        }
      >
        {label}
        <Icon className={`h-3 w-3 ${active ? "" : "opacity-30 print:hidden"}`} />
      </button>
    </th>
  );
}
